import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import {
  Business,
  Dashboard,
  DriveFileMove,
  Inventory,
  MedicalServices,
  MoveDown,
  NoteAlt,
  OpenInFull,
  Person,
  PersonAdd,
  ShoppingBasket,
  Store,
  LocalShipping,
} from '@mui/icons-material';
import { MenuItem, Select, SelectChangeEvent } from '@mui/material';

import { ICompanyInfo } from 'commons/types';
import { UserRolesEnum } from 'commons/types/user.roles.enum';
import { getWMSActiveLocations } from 'redux-stores/actions';
import { selectUser } from 'redux-stores/features/authSlice';
import { RootReducerInterface } from 'redux-stores/reducers';
import { setSelectedLocation } from 'redux-stores/reducers/dashboard.reducer';
import { AppDispatch } from 'redux-stores/store';
import { DASHBOARD_ROUTES } from 'routes/constants';

import { S } from './Sidebar.styles';

export interface ISidebarProps {
  currentUrl: string;
}

export const Sidebar: React.FC<ISidebarProps> = ({ currentUrl }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const user = useSelector(selectUser);
  const { selectedLocation, activeLocations } = useSelector(
    (state: RootReducerInterface) => state.dashboard,
  );

  useEffect(() => {
    if (activeLocations.length === 0) {
      dispatch(getWMSActiveLocations(user.warehouseId));
    }
  }, []);

  const handleSelectLocation = (e: SelectChangeEvent) => {
    const updatedSelectedLocation = activeLocations.find(
      (loc) => loc.warehouse_id === parseInt(e.target.value, 10),
    );
    dispatch(setSelectedLocation(updatedSelectedLocation as ICompanyInfo));
  };

  return (
    <S.Sidebar>
      <S.SidebarWrapper>
        <S.SidebarHeader>
          <S.SidebarHeaderTitle>{user.userName}</S.SidebarHeaderTitle>
          <S.SidebarHeaderSubtitle>
            {/* change with option list button */}
            {!user.roles.includes(UserRolesEnum.SUPER_ADMIN) ? (
              `Location: ${user.warehouse}`
            ) : (
              <Select
                onChange={handleSelectLocation}
                value={selectedLocation?.warehouse_id.toString() || ''}
                placeholder="Location"
                style={{ width: 200, marginTop: 8 }}
              >
                {activeLocations.map((loc) => (
                  <MenuItem value={loc.warehouse_id}>
                    {loc.abbreviation}
                  </MenuItem>
                ))}
              </Select>
            )}
          </S.SidebarHeaderSubtitle>
        </S.SidebarHeader>
        <S.SidebarMenu>
          <S.SidebarMenuItem
            color={
              currentUrl === DASHBOARD_ROUTES.SUMMARY ? 'primary' : 'default'
            }
            onClick={(): void => navigate(DASHBOARD_ROUTES.SUMMARY)}
          >
            <Dashboard />
            Dashboard
          </S.SidebarMenuItem>
          <S.SidebarMenuItem
            color={
              currentUrl === DASHBOARD_ROUTES.ORDER_ITEM ? 'primary' : 'default'
            }
            onClick={(): void => navigate(DASHBOARD_ROUTES.ORDER_ITEM)}
          >
            <ShoppingBasket />
            Order List
          </S.SidebarMenuItem>
          <S.SidebarMenuItem
            color={
              currentUrl === DASHBOARD_ROUTES.REPLENISH_ITEM
                ? 'primary'
                : 'default'
            }
            onClick={(): void => navigate(DASHBOARD_ROUTES.REPLENISH_ITEM)}
          >
            <MoveDown />
            Replenish List
          </S.SidebarMenuItem>
          <S.SidebarMenuItem
            color={
              currentUrl === DASHBOARD_ROUTES.STOCK_TRANSFER_ITEM
                ? 'primary'
                : 'default'
            }
            onClick={(): void => navigate(DASHBOARD_ROUTES.STOCK_TRANSFER_ITEM)}
          >
            <OpenInFull />
            Bin Transfer List
          </S.SidebarMenuItem>
          <S.SidebarMenuItem
            color={currentUrl === DASHBOARD_ROUTES.STO ? 'primary' : 'default'}
            onClick={(): void => navigate(DASHBOARD_ROUTES.STO)}
          >
            <LocalShipping />
            STO
          </S.SidebarMenuItem>
          <S.SidebarMenuItem
            color={
              currentUrl === DASHBOARD_ROUTES.USERS ? 'primary' : 'default'
            }
            onClick={(): void => navigate(DASHBOARD_ROUTES.USERS)}
          >
            <Person />
            User List
          </S.SidebarMenuItem>
          <S.SidebarMenuItem
            color={
              currentUrl === DASHBOARD_ROUTES.PRODUCT_UPLOAD
                ? 'primary'
                : 'default'
            }
            onClick={(): void => navigate(DASHBOARD_ROUTES.PRODUCT_UPLOAD)}
          >
            <MedicalServices />
            Product Upload
          </S.SidebarMenuItem>

          {user.roles.includes(UserRolesEnum.SUPER_ADMIN) && (
            <>
              <S.SidebarMenuItem
                color={
                  currentUrl === DASHBOARD_ROUTES.PHARMACY_UPLOAD
                    ? 'primary'
                    : 'default'
                }
                onClick={(): void => navigate(DASHBOARD_ROUTES.PHARMACY_UPLOAD)}
              >
                <Store />
                Pharmacy Upload
              </S.SidebarMenuItem>
              <S.SidebarMenuItem
                color={
                  currentUrl === DASHBOARD_ROUTES.USER_UPLOAD
                    ? 'primary'
                    : 'default'
                }
                onClick={(): void => navigate(DASHBOARD_ROUTES.USER_UPLOAD)}
              >
                <PersonAdd />
                User Upload
              </S.SidebarMenuItem>
              <S.SidebarMenuItem
                color={
                  currentUrl === DASHBOARD_ROUTES.COMPANY_INFO
                    ? 'primary'
                    : 'default'
                }
                onClick={(): void => navigate(DASHBOARD_ROUTES.COMPANY_INFO)}
              >
                <Business />
                Company Info
              </S.SidebarMenuItem>
              <S.SidebarMenuItem
                color={
                  currentUrl === DASHBOARD_ROUTES.INVENTORY_ADJUSTMENT
                    ? 'primary'
                    : 'default'
                }
                onClick={(): void =>
                  navigate(DASHBOARD_ROUTES.INVENTORY_ADJUSTMENT)
                }
              >
                <DriveFileMove />
                Inventory Adjustment
              </S.SidebarMenuItem>
            </>
          )}
          <S.SidebarMenuItem
            color={currentUrl === DASHBOARD_ROUTES.BINS ? 'primary' : 'default'}
            onClick={(): void => navigate(DASHBOARD_ROUTES.BINS)}
          >
            <Inventory />
            Master Bin
          </S.SidebarMenuItem>
          <S.SidebarMenuItem
            color={
              currentUrl === DASHBOARD_ROUTES.INBOUND ? 'primary' : 'default'
            }
            onClick={(): void => navigate(DASHBOARD_ROUTES.INBOUND)}
          >
            {currentUrl === DASHBOARD_ROUTES.INBOUND ? (
              <img
                src="/assets/inbound-dashboard-icon-blue.svg"
                alt="inbound-dashboard-icon-blue"
              />
            ) : (
              <img
                src="/assets/inbound-dashboard-icon-black.svg"
                alt="inbound-dashboard-icon-black"
              />
            )}
            Inbound Task List
          </S.SidebarMenuItem>
          <S.SidebarMenuItem
            color={
              currentUrl === DASHBOARD_ROUTES.DELIVERY_NOTE
                ? 'primary'
                : 'default'
            }
            onClick={(): void => navigate(DASHBOARD_ROUTES.DELIVERY_NOTE)}
          >
            <NoteAlt />
            Delivery Note List
          </S.SidebarMenuItem>
        </S.SidebarMenu>
      </S.SidebarWrapper>
    </S.Sidebar>
  );
};
